import moment from 'moment';
import theme from '../../ordersTheme.module.scss';

/**
 * The row expansion.
 *
 * Every value here is already in the `getOrders` payload the queue renders —
 * this opens no query. It exists so the seven fee columns, the four timestamps
 * and the broker detail stop being the reason an operator scrolls sideways
 * through 28 columns to answer one question about one order.
 *
 * Laid out as stacked label-over-value rather than label-left/value-right:
 * the right-aligned version left a wide empty gutter down the middle of every
 * column and read as a dot-leader table with the dots missing.
 */

const num = (value, decimals) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return Number(value).toLocaleString(
    undefined,
    decimals === undefined
      ? undefined
      : {minimumFractionDigits: decimals, maximumFractionDigits: decimals},
  );
};

const when = value =>
  value ? moment(value).format('DD MMM YYYY, h:mmA') : '—';

const Item = ({label, value, wide}) => (
  <div className={`${theme.dItem} ${wide ? theme.dItemWide : ''}`}>
    <span className={theme.dLabel}>{label}</span>
    <span className={theme.dValue}>{value}</span>
  </div>
);

const Block = ({title, children}) => (
  <section className={theme.dBlock}>
    <h4 className={theme.dTitle}>{title}</h4>
    <div className={theme.dItems}>{children}</div>
  </section>
);

export const OrderRowDetail = ({record}) => {
  const brokerIds = record?.brokerIds ?? [];
  const automatic = brokerIds.length > 0;
  const quantity = Number(record?.quantity) || 0;
  const filled = Number(record?.fillQuantity) || 0;
  const unitPrice = record?.avgFillPrice || record?.limitPrice;
  const progress = quantity > 0 ? Math.min(100, (filled / quantity) * 100) : 0;

  return (
    <div className={theme.detail}>
      {/* The four things someone opens a row to check, before the detail. */}
      <div className={theme.dStats}>
        <div className={theme.dStat}>
          <span className={theme.dStatLabel}>Filled</span>
          <span className={theme.dStatValue}>
            {num(filled)} <em>of {num(quantity)}</em>
          </span>
          <span className={theme.dBar}>
            <span className={theme.dBarFill} style={{width: `${progress}%`}} />
          </span>
        </div>
        <div className={theme.dStat}>
          <span className={theme.dStatLabel}>Order value</span>
          <span className={theme.dStatValue}>
            {num(quantity * unitPrice, 2)}
            <em>{record?.assetCurrency ? ` ${record.assetCurrency}` : ''}</em>
          </span>
        </div>
        <div className={theme.dStat}>
          <span className={theme.dStatLabel}>Fees charged</span>
          <span className={theme.dStatValue}>{num(record?.feeCharged, 2)}</span>
        </div>
        <div className={theme.dStat}>
          <span className={theme.dStatLabel}>Route</span>
          <span className={theme.dStatValue}>
            <span
              className={`${theme.pill} ${theme.pillDot} ${
                automatic ? theme.toneOk : theme.toneNeutral
              }`}>
              {automatic ? 'AUTOMATIC' : 'MANUAL'}
            </span>
          </span>
        </div>
      </div>

      <div className={theme.dCols}>
        <Block title="Fees">
          <Item label="Static" value={num(record?.staticFee, 2)} />
          <Item label="Floating" value={num(record?.floatingFee, 2)} />
          <Item label="Recurring" value={num(record?.recurringFee, 2)} />
          <Item label="Commission" value={num(record?.commissionFee, 2)} />
          <Item label="Entry" value={num(record?.entryFee, 2)} />
          <Item label="Exit" value={num(record?.exitFee, 2)} />
          <Item label="Total" value={num(record?.totalFee, 2)} />
        </Block>

        <Block title="Pricing">
          <Item label="Limit price" value={num(record?.limitPrice, 2)} />
          <Item label="Average fill" value={num(record?.avgFillPrice, 2)} />
          <Item label="Currency" value={record?.assetCurrency || '—'} />
          <Item
            label="Asset class"
            value={record?.assetClass?.replace(/_/g, ' ') || '—'}
          />
        </Block>

        <Block title="Timing">
          <Item wide label="Created" value={when(record?.createdAt)} />
          <Item wide label="Expires" value={when(record?.expiresAt)} />
          <Item wide label="Last fill" value={when(record?.lastFillDate)} />
          <Item wide label="Settled" value={when(record?.settlementDate)} />
        </Block>

        <Block title="Broker">
          <Item wide label="Broker" value={record?.broker?.name || '—'} />
          <Item
            wide
            label="Broker order"
            value={record?.brokerOrderId || '—'}
          />
          <Item label="Broker IDs" value={brokerIds.length || '—'} />
          <Item label="Retried" value={record?.retry ? 'Yes' : 'No'} />
          <Item
            wide
            label="Collection"
            value={record?.collection?.name || '—'}
          />
        </Block>
      </div>

      {record?.notes?.note && (
        <div className={theme.dNote}>
          <span className={theme.dLabel}>
            Note
            {record?.notes?.createdBy?.firstName
              ? ` · ${record.notes.createdBy.firstName} ${
                  record.notes.createdBy.lastName ?? ''
                }`.trimEnd()
              : ''}
          </span>
          <p>{record.notes.note}</p>
        </div>
      )}
    </div>
  );
};
